import { useEffect, useRef, useState } from "react";
import type { FC, ReactNode } from "react";

import { StatusEnum } from "./types";
import { useIsomorphicLayoutEffect } from "../../../hooks";

import type { ITransitionProps } from "./types";

/**
 * Компонент, который добавляет/убирает классы элементу (проп refElement) для анимаций появления/ухода,
 * ориентируется на проп isShow, показывает/скрывает children.
 *
 * Состояния компонента перечислены в StatusEnum.
 * @author se.sokolov@vk.team
 */
export const Transition: FC<ITransitionProps> = ({
    timeout,
    timeoutForExit,
    refElement,
    classes,
    isShow,
    children,
    onEntered,
    onExited,
    skipFirstRender = false,
    alwaysInDOM = false,
}): ReactNode => {
    const skipRender = useRef(skipFirstRender);
    const [status, setStatus] = useState<StatusEnum | null>(() => {
        if (!skipFirstRender) {
            return null;
        }

        return isShow ? StatusEnum.ENTER_DONE : StatusEnum.EXIT_DONE;
    });

    useEffect(() => {
        if (skipRender.current) {
            skipRender.current = false;
            return;
        }

        let enterTimeout: number;
        let exitTimeout: number;

        if (isShow) {
            setStatus(StatusEnum.ENTER_ACTIVE);

            enterTimeout = window.setTimeout(() => {
                setStatus(StatusEnum.ENTER_DONE);
                onEntered?.();
            }, timeout);
        } else {
            setStatus(StatusEnum.EXIT_ACTIVE);

            exitTimeout = window.setTimeout(() => {
                setStatus(StatusEnum.EXIT_DONE);
                onExited?.();
            }, timeoutForExit || timeout);
        }

        return () => {
            clearTimeout(enterTimeout);
            clearTimeout(exitTimeout);
        };
    }, [isShow, timeout, timeoutForExit, skipRender, onEntered, onExited]);

    useIsomorphicLayoutEffect(() => {
        const element = refElement.current;
        if (!element) {
            return;
        }

        const elementClassList = element.classList;

        const { enterActive, enterDone, exitActive, exitDone } = classes;

        switch (status) {
            case StatusEnum.ENTER_ACTIVE:
                if (exitDone) {
                    elementClassList.remove(exitDone);
                }
                /** Анимация закрытия может не успеть закончиться, а isShow уже снова станет true */
                if (exitActive) {
                    elementClassList.remove(exitActive);
                    element.removeAttribute("aria-hidden");
                }
                elementClassList.add(enterActive);
                break;
            case StatusEnum.ENTER_DONE:
                elementClassList.remove(enterActive);
                elementClassList.add(enterDone);
                break;
            case StatusEnum.EXIT_ACTIVE:
                element.setAttribute("aria-hidden", "true");
                elementClassList.remove(enterDone);
                elementClassList.add(exitActive);
                break;
            case StatusEnum.EXIT_DONE:
                element.removeAttribute("aria-hidden");
                elementClassList.remove(exitActive);
                if (exitDone) {
                    elementClassList.add(exitDone);
                }
                break;
            default:
        }
    }, [status, classes, refElement]);

    return status !== StatusEnum.EXIT_DONE || alwaysInDOM ? children : null;
};
